import type { Classification } from './types';

/** Deterministic colour per department, so the swatch is a recognisable anchor. */
export function deptColor(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i += 1) hash = (hash * 31 + slug.charCodeAt(i)) % 360;
  return `hsl(${hash} 52% 30%)`;
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
}

export const CLASSIFICATION_ORDER: Classification[] = ['public', 'internal', 'confidential', 'restricted'];

export function classificationPill(c: Classification): string {
  switch (c) {
    case 'restricted':
      return 'pill pill-danger';
    case 'confidential':
      return 'pill pill-warn';
    case 'public':
      return 'pill pill-ok';
    default:
      return 'pill';
  }
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 45) return 'just now';
  const units: [number, string][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
  ];
  let value = seconds;
  let unit = 'second';
  for (const [divisor, name] of units) {
    if (value < divisor) break;
    value = value / divisor;
    unit = name === 'second' ? 'minute' : nextUnit(name);
  }
  const rounded = Math.round(value);
  return `${rounded} ${unit}${rounded === 1 ? '' : 's'} ago`;
}

function nextUnit(unit: string): string {
  const chain: Record<string, string> = {
    second: 'minute',
    minute: 'hour',
    hour: 'day',
    day: 'week',
    week: 'month',
    month: 'year',
  };
  return chain[unit] ?? unit;
}

export function fileNameOf(path: string): string {
  return (path.split('/').pop() ?? path).replace(/\.mdx?$/i, '');
}

export function folderOf(path: string): string {
  const parts = path.split('/');
  parts.pop();
  return parts.join('/');
}

/** Friendly navigation labels; actual paths and links remain unchanged. */
export function folderLabel(path: string): string {
  return path.split('/').filter(Boolean).map((part) => {
    const label = part.replace(/^\d{2,3}[-_ ]+/, '').replace(/[-_]+/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  }).join(' / ') || 'General';
}

/** Format a frontmatter value for the metadata panel. */
export function displayValue(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let timer: number | undefined;
  return (...args: A) => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), ms);
  };
}

/**
 * Order guard for overlapping requests: each call starts a request and
 * returns a check that is true only while it is still the latest one, so an
 * older, slower reply cannot overwrite a newer one.
 */
export function latestOnly(): () => () => boolean {
  let latest = 0;
  return () => {
    const id = ++latest;
    return () => id === latest;
  };
}

/**
 * A navigation that waits for a server reply, such as following a link in
 * the editor. Only the newest request may act, and only while the person is
 * still at the address where they started it (`where`, the router's location
 * key); otherwise the reply is dropped and `run` gives null. `pending` says
 * whether a request is out, for feedback. `cancel` drops any request, for
 * when the page goes (FC-4).
 */
export function pendingNavigation(where: () => string, pending: (out: boolean) => void) {
  const next = latestOnly();
  return {
    async run<T>(request: () => Promise<T>): Promise<T | null> {
      const current = next(); const from = where();
      pending(true);
      try {
        const result = await request();
        return current() && where() === from ? result : null;
      } finally { if (current()) pending(false); }
    },
    cancel() { next(); },
  };
}

export interface ReindexStats { scanned: number; updated: number; removed: number; errors?: { path: string; error: string }[] }

/** What a reindex did, including the files it could not index. */
export function reindexSummary(stats: Record<string, ReindexStats>): { message: string; failures: { dept: string; path: string; error: string }[] } {
  const all = Object.entries(stats);
  const updated = all.reduce((sum, [, s]) => sum + s.updated, 0);
  const removed = all.reduce((sum, [, s]) => sum + s.removed, 0);
  const failures = all.flatMap(([dept, s]) => (s.errors ?? []).map((e) => ({ dept, path: e.path, error: e.error })));
  const failed = failures.length ? `; ${failures.length === 1 ? '1 file' : `${failures.length} files`} could not be indexed` : '';
  return { message: `Reindexed: ${updated} updated, ${removed} removed${failed}`, failures };
}

/**
 * Name the fields a validation failure is about. The server's generic
 * message ("Request body failed validation") says nothing a person can fix.
 */
export function describeValidation(message: string, issues: { path: string; message: string }[] | undefined): string {
  if (!issues?.length) return message;
  const shown = issues.slice(0, 3).map((issue) => (issue.path ? `${issue.path}: ${issue.message}` : issue.message));
  const more = issues.length > 3 ? ` (and ${issues.length - 3} more)` : '';
  return `${message}. ${shown.join('; ')}${more}`;
}

/**
 * Note paths contain slashes and spaces; encode each segment, keep the slashes.
 *
 * A lone surrogate (a title cut mid-emoji by the server's 120-unit limit)
 * made encodeURIComponent throw, so "Create note" reported a failure for a
 * note it had just created. It is encoded as U+FFFD instead: a URL cannot
 * carry a lone surrogate, and an encoder that throws while drawing a link
 * would take the page down with it.
 */
export function encodeNotePath(path: string): string {
  const wellFormed = path.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '\uFFFD');
  return wellFormed.split('/').map(encodeURIComponent).join('/');
}
